import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { usersApi, usersQueryKeys } from '../api';
import type {
  UserFilters,
  CreateUserDto,
  UpdateUserDto,
  ChangeOwnPasswordDto,
  ChangeUserPasswordDto,
} from '../types';

// Hook for fetching users list
export function useUsers(filters?: UserFilters) {
  return useQuery({
    queryKey: usersQueryKeys.list(filters),
    queryFn: () => usersApi.getAll(filters),
  });
}

// Hook for fetching single user
export function useUser(id: number | string) {
  return useQuery({
    queryKey: usersQueryKeys.detail(id),
    queryFn: () => usersApi.getById(id),
    enabled: !!id,
  });
}

// Hook for creating user
export function useCreateUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateUserDto) => usersApi.create(data),
    onSuccess: () => {
      toast.success(t('createdSuccess', { entity: t('user') }));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error);
      if (message?.includes('phone')) {
        toast.error(t('phoneAlreadyExists'));
      } else {
        toast.error(message || t('createError', { error: 'Unknown error' }));
      }
    },
  });
}

// Hook for updating user
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateUserDto }) =>
      usersApi.update({ id, data }),
    onSuccess: (_, variables) => {
      toast.success(t('updatedSuccess', { entity: t('user') }));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}

// Hook for deleting user
export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => usersApi.delete(id),
    onSuccess: () => {
      toast.success(t('deletedSuccess', { entity: t('user') }));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('deleteError', { error: 'Unknown error' })));
    },
  });
}

// Hook for changing own password
export function useChangeOwnPassword() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ChangeOwnPasswordDto) => usersApi.changeOwnPassword(data),
    onSuccess: () => {
      toast.success(t('passwordChanged'));
      // Clear tokens and redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      queryClient.clear();
      window.location.href = '/';
    },
    onError: (error: unknown) => {
      const msg = getApiErrorMessage(error);

      switch (msg) {
        case 'Old password is incorrect':
          toast.error(t('oldPasswordIncorrect'));
          break;
        case 'oldPassword must be longer than or equal to 8 characters':
          toast.error(t('oldPasswordTooShort'));
          break;
        case 'newPassword must be longer than or equal to 8 characters':
          toast.error(t('newPasswordTooShort'));
          break;
        default:
          toast.error(msg || t('badRequest'));
      }
    },
  });
}

// Hook for changing another user's password
export function useChangeUserPassword() {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: ChangeUserPasswordDto }) =>
      usersApi.changeUserPassword({ id, data }),
    onSuccess: () => {
      toast.success(t('passwordChanged'));
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}

// Hook for activating user
export function useActivateUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => usersApi.activate(id),
    onSuccess: () => {
      toast.success(t('userActivated'));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}

// Hook for blocking user
export function useBlockUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => usersApi.block(id),
    onSuccess: () => {
      toast.success(t('userBlocked'));
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}
